import { GameConstants } from "../../../GameConstants";
import { BattleManager } from "../BattleManager";
import { GameVars } from "../../../GameVars";
import * as Anuto from "../../../../engine/src";
import { ConstructionBar } from "./ConstructionBar";
import { BattleScene } from "../BattleScene";
import { AudioManager } from "../../../AudioManager";

export class TurretActor extends Phaser.GameObjects.Container {


    public id: number;
    public level: number;
    public p: {r: number, c: number};
    public canonLength: number;
    public base: Phaser.GameObjects.Sprite;
    public cannon: Phaser.GameObjects.Sprite;
    public anutoTurret: Anuto.Turret;
    
    protected rangeCircle: Phaser.GameObjects.Image;
    protected constructionBar: ConstructionBar;
    protected workInProgressSprite: Phaser.GameObjects.Sprite;
    protected canUpgradeImage: Phaser.GameObjects.Sprite;

    constructor(scene: Phaser.Scene, type: string, position: {r: number, c: number}, turret: Anuto.Turret, textureAtlas: string, hasCannon: boolean) {

        super(scene);

        this.p = position;
        this.name = type;
        
        this.anutoTurret = turret;
        this.id = this.anutoTurret.id;

        this.x = GameConstants.CELLS_SIZE * (this.p.c + .5);
        this.y = GameConstants.CELLS_SIZE * (this.p.r + .5);

        this.canonLength = 40;

        this.rangeCircle = BattleManager.createRangeCircle(this.anutoTurret.range * GameConstants.CELLS_SIZE, this.x, this.y);

        this.base = new Phaser.GameObjects.Sprite(this.scene, 0, 0, textureAtlas);
        this.base.setInteractive({pixelPerfect: true});
        this.base.on("pointerdown", this.onDownTurret, this);
        this.base.visible = false;
        BattleScene.currentInstance.add.existing(this.base);
        this.add(this.base);

        if (hasCannon) {
            this.cannon = new Phaser.GameObjects.Sprite(this.scene, 0, 0, textureAtlas);
            this.cannon.visible = false;
            BattleScene.currentInstance.add.existing(this.cannon);
            this.add(this.cannon);
        } else {
            this.cannon = null;
        }
       
        this.workInProgressSprite = new Phaser.GameObjects.Sprite(this.scene, 0, 0, "texture_atlas_1", "progress_01");
        this.scene.add.existing(this.workInProgressSprite);
        this.workInProgressSprite.play("work_in_progress");
        this.add(this.workInProgressSprite);

        this.constructionBar = new ConstructionBar(this.scene, this);
        this.add(this.constructionBar);

        this.canUpgradeImage = this.scene.add.sprite(20, 20, "texture_atlas_1");
        this.canUpgradeImage.visible = false;
        this.add(this.canUpgradeImage);

        this.canUpgradeImage.anims.play("upgrade_arrow");
    }

    public update(time: number, delta: number): void {
    
        if (this.anutoTurret.constructing || this.anutoTurret.upgrading) {

            this.constructionBar.visible = true;
            this.constructionBar.update();

            this.canUpgradeImage.visible = false;
        } else {
            if (this.anutoTurret.level < this.anutoTurret.maxLevel && BattleManager.anutoEngine.credits >= this.anutoTurret.priceUpgrade && GameVars.gameData.upgradesUnlocked[this.anutoTurret.type] >= this.anutoTurret.level + 1) {
                this.canUpgradeImage.visible = true;
            } else {
                this.canUpgradeImage.visible = false;
            }
        }
    }

    public onReady(): void {

        this.constructionBar.visible = false;
       
        this.workInProgressSprite.destroy();

        this.base.visible = true;

        if (this.cannon) {
            this.cannon.visible = true;
        }
        
        this.scaleX = .75;
        this.scaleY = .5;

        this.scene.tweens.add({
            targets: this,
            scaleY: 1,
            scaleX: 1,
            ease: Phaser.Math.Easing.Back.Out,
            duration: 300
        });

        this.scene.tweens.add({
            targets: this,
            alpha: 1,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 300
        });

        AudioManager.playSound("build_upgrade_complete");

        this.addSmokeParticles();
    }

    public setToUpgrade(): void {

        BattleManager.hideTurretMenu();

        const scale = this.anutoTurret.getRangeAfterUpgrade() * GameConstants.CELLS_SIZE * 2 / 196;

        this.scene.tweens.add({
            targets: this.rangeCircle,
            scaleX: scale,
            scaleY: scale,
            ease: Phaser.Math.Easing.Elastic.Out,
            duration: 600,
            onComplete: function(): void {
                this.scene.tweens.add({
                    targets: this.rangeCircle,
                    alpha: 0,
                    ease: Phaser.Math.Easing.Cubic.Out,
                    delay: 300,
                    duration: 450,
                    onComplete: function(): void {
                        this.rangeCircle.visible = false;
                    },
                    onCompleteScope: this
                }); 
            },
            onCompleteScope: this
        }); 
    }

    public upgrade(): void {

        this.constructionBar.visible = false;
        
        this.scaleX = .75;
        this.scaleY = .5;

        this.scene.tweens.add({
            targets: this,
            scaleY: 1,
            scaleX: 1,
            ease: Phaser.Math.Easing.Back.Out,
            duration: 300
        });

        this.addSmokeParticles();

        AudioManager.playSound("build_upgrade_complete");
    }

    public reloadRangeCircle(): void {

        this.rangeCircle.setScale((this.anutoTurret.range * GameConstants.CELLS_SIZE * 2) / 196);
    }

    protected addSmokeParticles(): void {

        for (let i = 0; i < 5; i ++) {

            const px = 10 - 20 * Math.random();
            const py = -20 * Math.random();
            const scale = .45 + .35 * Math.random();
            const dy = 40 + 30 * Math.random();
            const delay = 150 * Math.random();

            const smokeParticle = new Phaser.GameObjects.Image(this.scene, px, py, "texture_atlas_3", "smoke_particle2");
            smokeParticle.angle = 360 * Math.random();
            smokeParticle.setScale(0);
            this.add(smokeParticle);

            this.scene.tweens.add({
                targets: smokeParticle,
                alpha: 1,
                scaleY: scale,
                scaleX: scale,
                ease: Phaser.Math.Easing.Cubic.Out,
                delay: delay,
                duration: 200
            });

            this.scene.tweens.add({
                targets: smokeParticle,
                y: py - dy,
                ease: Phaser.Math.Easing.Cubic.Out,
                delay: delay,
                duration: 800,
                onComplete: () => {
                    smokeParticle.destroy();
                },
                onCompleteScope: this
            });

            this.scene.tweens.add({
                targets: smokeParticle,
                alpha: 0,
                ease: Phaser.Math.Easing.Cubic.Out,
                delay: 400 + delay,
                duration: 500
            });
        }
    }

    protected shoot(): void {
        // 
    }

    protected onDownTurret(): void {

        if (this.anutoTurret.constructing || this.anutoTurret.upgrading || GameVars.paused || BattleManager.anutoEngine.gameOver || BattleManager.anutoEngine.win) {
            return;
        }

        if (!this.rangeCircle.visible) {
            BattleManager.hideRangeCircles();
            this.rangeCircle.visible = true;
            BattleManager.showTurretMenu(this.anutoTurret);
        } else {
            BattleManager.hideRangeCircles();
            BattleManager.hideTurretMenu();
        }

        BattleManager.hideSelectTurret();

        AudioManager.playSound("btn_click");
    }
}
